import { BlockBlobClient } from '@azure/storage-blob'

const DEFAULT_BLOCK_SIZE_BYTES = 8 * 1024 * 1024
const MAX_CONCURRENT_CHUNKS = 3
const MAX_CHUNK_ATTEMPTS = 4
const CHUNK_RETRY_DELAY_MS = 1000

export interface ResumableBlobUploadOptions {
  blockSizeBytes?: number
  onProgress?: (loaded: number, total: number) => void
}

const buildBlockId = (index: number): string => {
  const padded = index.toString().padStart(10, '0')
  return btoa(`block-${padded}`)
}

export class ResumableBlobUpload {
  private readonly file: File | Blob
  private readonly blockBlobClient: BlockBlobClient
  private readonly blockSizeBytes: number
  private readonly onProgress?: (loaded: number, total: number) => void
  private readonly totalChunks: number
  private readonly stagedChunks = new Set<number>()
  private activeAbortControllers = new Set<AbortController>()
  private paused = false
  private inFlight: Promise<void> | null = null
  private resumeWaiters: Array<() => void> = []

  constructor(file: File | Blob, uploadUrl: string, options?: ResumableBlobUploadOptions) {
    this.file = file
    this.blockBlobClient = new BlockBlobClient(uploadUrl)
    this.blockSizeBytes = options?.blockSizeBytes ?? DEFAULT_BLOCK_SIZE_BYTES
    this.onProgress = options?.onProgress
    this.totalChunks = Math.max(1, Math.ceil(this.file.size / this.blockSizeBytes))
  }

  private getChunk(index: number): Blob {
    const start = index * this.blockSizeBytes
    const end = Math.min(start + this.blockSizeBytes, this.file.size)
    return this.file.slice(start, end)
  }

  private reportProgress() {
    if (!this.onProgress) return
    const loaded = Array.from(this.stagedChunks).reduce((sum, index) => sum + this.getChunk(index).size, 0)
    this.onProgress(loaded, this.file.size)
  }

  private async stageChunk(index: number): Promise<void> {
    if (this.stagedChunks.has(index)) return

    const chunk = this.getChunk(index)
    for (let attempt = 1; ; attempt++) {
      const abortController = new AbortController()
      this.activeAbortControllers.add(abortController)
      try {
        await this.blockBlobClient.stageBlock(buildBlockId(index), chunk, chunk.size, {
          abortSignal: abortController.signal,
        })
        this.stagedChunks.add(index)
        this.reportProgress()
        return
      } catch (error) {
        const status = (error as { statusCode?: number })?.statusCode
        const retryable = !this.paused && status !== 403 && status !== 401
        if (!retryable || attempt >= MAX_CHUNK_ATTEMPTS) throw error
        await new Promise(resolve => setTimeout(resolve, CHUNK_RETRY_DELAY_MS * attempt))
      } finally {
        this.activeAbortControllers.delete(abortController)
      }
    }
  }

  async start(): Promise<void> {
    if (this.inFlight) return this.inFlight

    this.paused = false
    this.inFlight = this.run()
    try {
      await this.inFlight
    } finally {
      this.inFlight = null
    }
  }

  private waitForResume(): Promise<void> {
    return new Promise(resolve => this.resumeWaiters.push(resolve))
  }

  private async run(): Promise<void> {
    try {
      const queue = Array.from({ length: this.totalChunks }, (_, index) => index).filter(
        index => !this.stagedChunks.has(index),
      )

      const worker = async (): Promise<void> => {
        for (;;) {
          if (this.paused) {
            await this.waitForResume()
            continue
          }
          const index = queue.shift()
          if (index === undefined) return
          try {
            await this.stageChunk(index)
          } catch (error) {
            if (this.paused) {
              queue.unshift(index)
              continue
            }
            throw error
          }
        }
      }

      await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT_CHUNKS, this.totalChunks) }, worker))

      const orderedBlockIds = Array.from({ length: this.totalChunks }, (_, index) => buildBlockId(index))
      await this.blockBlobClient.commitBlockList(orderedBlockIds)
    } catch (error) {
      throw error instanceof Error ? error : new Error('Azure Blob upload failed')
    }
  }

  pause(): void {
    this.paused = true
    this.activeAbortControllers.forEach(controller => controller.abort())
  }

  async resume(): Promise<void> {
    if (this.inFlight) {
      this.paused = false
      const waiters = this.resumeWaiters.splice(0)
      waiters.forEach(resolve => resolve())
      return this.inFlight
    }
    return this.start()
  }
}

export const uploadFileToBlob = (file: Blob, uploadUrl: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob')

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Blob upload failed with status ${xhr.status}`))
      }
    }
    xhr.onerror = () => reject(new Error('Blob upload failed'))

    xhr.send(file)
  })
}
